import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { X, Plus, Minus, Trash2, ShoppingBag } from "lucide-react";
import { useStore } from "../contexts/StoreContext";
import { useLang } from "../i18n";
import { formatPrice } from "../lib/api";
import { OBI } from "../assets/obi";

const CartDrawer = () => {
  const { cart, cartOpen, setCartOpen, removeFromCart, updateQty, subtotal } = useStore();
  const { t } = useLang();
  const navigate = useNavigate();

  if (!cartOpen) return null;

  const goCheckout = () => {
    setCartOpen(false);
    navigate("/checkout");
  };

  return (
    <div
      data-testid="cart-drawer"
      className="fixed inset-0 z-50 flex justify-end"
      role="dialog"
    >
      <div
        className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm"
        onClick={() => setCartOpen(false)}
      />
      <aside className="relative w-full max-w-md h-full bg-amber-50 border-l-4 border-slate-900 flex flex-col animate-fade-up">
        <div className="flex items-center justify-between p-5 border-b-4 border-slate-900 bg-sky-300">
          <div className="flex items-center gap-2">
            <ShoppingBag className="w-6 h-6" strokeWidth={2.5} />
            <h2 className="font-display font-bold text-2xl text-slate-900">{t("cart.title")}</h2>
          </div>
          <button
            data-testid="close-cart-button"
            onClick={() => setCartOpen(false)}
            className="w-10 h-10 bg-white border-4 border-slate-900 rounded-full flex items-center justify-center cartoon-shadow-sm hover:bg-yellow-200 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {cart.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center p-8">
            <img src={OBI.shopping} alt="OBI" className="w-48 h-48 object-contain animate-float" />
            <h3 className="font-display font-bold text-2xl mt-4 text-slate-900">{t("cart.empty")}</h3>
            <p className="text-slate-600 mt-2">{t("cart.empty_sub")}</p>
            <Link
              to="/catalog"
              onClick={() => setCartOpen(false)}
              className="cartoon-btn bg-yellow-300 mt-6"
            >
              {t("cart.continue_shopping")}
            </Link>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto p-5 space-y-4">
              {cart.map((item) => (
                <div
                  key={item.id}
                  data-testid={`cart-item-${item.id}`}
                  className="flex gap-3 bg-white border-4 border-slate-900 rounded-2xl p-3 cartoon-shadow-sm"
                >
                  <div className="w-20 h-20 bg-pink-100 border-2 border-slate-900 rounded-xl overflow-hidden flex-shrink-0">
                    {item.image && (
                      <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-bold text-sm leading-tight line-clamp-2">{item.name}</h4>
                    <p className="font-display font-bold text-base text-slate-900 mt-1">
                      {formatPrice(item.price * item.quantity)}
                    </p>
                    <div className="flex items-center justify-between gap-2 mt-2">
                      <div className="flex items-center gap-1 bg-yellow-100 border-2 border-slate-900 rounded-full p-0.5">
                        <button
                          data-testid={`qty-decrease-${item.id}`}
                          onClick={() => updateQty(item.id, item.quantity - 1)}
                          className="w-7 h-7 bg-white rounded-full border-2 border-slate-900 flex items-center justify-center hover:bg-yellow-300"
                        >
                          <Minus className="w-3 h-3" strokeWidth={3} />
                        </button>
                        <span className="px-2 font-bold text-sm w-6 text-center">{item.quantity}</span>
                        <button
                          data-testid={`qty-increase-${item.id}`}
                          onClick={() => updateQty(item.id, item.quantity + 1)}
                          className="w-7 h-7 bg-white rounded-full border-2 border-slate-900 flex items-center justify-center hover:bg-yellow-300"
                        >
                          <Plus className="w-3 h-3" strokeWidth={3} />
                        </button>
                      </div>
                      <button
                        data-testid={`cart-remove-${item.id}`}
                        onClick={() => removeFromCart(item.id)}
                        className="w-8 h-8 text-pink-600 hover:bg-pink-100 rounded-full flex items-center justify-center"
                        title={t("cart.remove")}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="border-t-4 border-slate-900 p-5 bg-white space-y-4">
              <div className="flex items-center justify-between">
                <span className="font-bold text-slate-700">{t("cart.subtotal")}</span>
                <span data-testid="cart-subtotal" className="font-display font-bold text-2xl text-slate-900">
                  {formatPrice(subtotal)}
                </span>
              </div>
              <button
                data-testid="cart-checkout-button"
                onClick={goCheckout}
                className="cartoon-btn bg-pink-400 hover:bg-pink-300 text-slate-900 w-full text-lg py-3"
              >
                {t("cart.checkout")}
              </button>
            </div>
          </>
        )}
      </aside>
    </div>
  );
};

export default CartDrawer;
